import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { DocumentIcon, Plus, TrashIcon } from 'assets/icons';
import Button from 'components/button';
import PanelCard, { cardIconButtonClass } from 'components/card/PanelCard';
import { fillText } from 'components/invoiceImport/invoiceImportText';
import { downloadFile } from 'components/invoiceList/useInvoiceExport';
import { formatParisDay } from 'components/invoiceList/invoiceStatusTooltipText';
import useModal from 'components/modal/useModal';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';
import {
  APPENDIX_ACCEPT,
  INVOICE_APPENDIX_DELETED,
  appendixErrorText,
  appendixFileRefusal,
  formatAppendixSize,
} from './invoiceAppendixText';
import useInvoiceAppendices from './useInvoiceAppendices';

// One key per appendix: a shared one would let a download cancel another.
export const appendixFileAbortKey = (appendixId) =>
  `admin/invoices/:number/appendices/${appendixId}/file`;

const STATUSES = {
  sent: {
    fallback: 'Envoyée dans Pennylane',
    className: 'bg-green-50 text-green-800',
  },
  pending: {
    fallback: 'En attente d’import',
    className: 'bg-gray-100 text-gray-700',
  },
  unconfirmed: {
    fallback: 'À vérifier dans Pennylane',
    className: 'bg-yellow-50 text-yellow-800',
  },
};

const ADDED_TEXTS = {
  sent: 'Annexe ajoutée et envoyée dans Pennylane.',
  pending: 'Annexe ajoutée : elle sera envoyée dans Pennylane à l’import.',
  skipped:
    'Ajoutée dans Jobbiz ; rien n’est envoyé à Pennylane hors production.',
  other: 'Annexe ajoutée.',
};

// The refusals after which the list on screen is no longer the api's.
const STALE_LIST_CODES = ['APPENDIX_NOT_FOUND', 'APPENDIX_NOT_UNCONFIRMED'];

const DownloadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 4v11m0 0l-4-4m4 4l4-4M5 19h14"
      stroke="#374151"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const fullName = (author) =>
  [author?.firstname, author?.lastname]
    .filter((part) => typeof part === 'string' && part.trim())
    .join(' ');

const errorCodeOf = (error) => error?.response?.data?.errorCode;

/**
 * The « Annexes » of an invoice (JBWEB-1277), in its « Informations » tab:
 * the files joined to it, added one at a time by a drop or the « + », each
 * downloadable and deletable. An appendix the import sent without an answer
 * from Pennylane is « à vérifier » until an admin, having found it there,
 * says so (decision 57: the screen never checks Pennylane itself).
 * `onConfirmed` runs after such a confirmation, which can close the
 * invoice's ERR_PENNYLANE_ANNEXE.
 */
const InvoiceAppendices = ({ invoice, onConfirmed }) => {
  const { language, setModalData, modalDataReturn, setModalDataReturn } =
    UseContext();
  const { toggle: toggleModal } = useModal();
  const text = (key, fallback) =>
    language.getValue(`invoiceAppendices.${key}`, fallback);
  const { number } = invoice;
  const appendices = useInvoiceAppendices(number);
  const { items, reload, add, replace, remove } = appendices;

  const inputRef = useRef(null);
  const mountedRef = useRef(true);
  const downloadKeysRef = useRef(new Set());
  const [uploadingName, setUploadingName] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [failure, setFailure] = useState(null);
  const [downloadingIds, setDownloadingIds] = useState([]);
  const [confirmingIds, setConfirmingIds] = useState([]);
  const isUploading = uploadingName !== null;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      downloadKeysRef.current.forEach((key) => ApiDatabase.abortRequest(key));
    };
  }, []);

  // The delete modal answers with the appendix it deleted.
  useEffect(() => {
    if (modalDataReturn?.type !== INVOICE_APPENDIX_DELETED) return;
    if (Number(modalDataReturn.number) === number) {
      if (modalDataReturn.reload) reload();
      else remove(modalDataReturn.appendixId);
    }
    setModalDataReturn({});
  }, [modalDataReturn, number, reload, remove, setModalDataReturn]);

  const showFailure = (code, failureKey) =>
    setFailure(appendixErrorText(text, code, failureKey));

  const upload = (file) => {
    const refusal = appendixFileRefusal(file);
    if (refusal) {
      showFailure(refusal, 'addFailed');
      return;
    }
    setFailure(null);
    setUploadingName(file.name);
    ApiDatabase.postInvoiceAppendix(
      { number, file },
      (data) => {
        const sync = data?.pennylaneSync;
        const key = ['sent', 'pending', 'skipped'].includes(sync)
          ? sync
          : 'other';
        // The toast outlives the section: the answer is told even after a
        // tab change, the list is read again when the section comes back.
        toast.success(text(`added.${key}`, ADDED_TEXTS[key]));
        if (!mountedRef.current) return;
        setUploadingName(null);
        if (data?.appendix && items !== null) add(data.appendix);
        else reload();
      },
      (error) => {
        if (error?.name === 'CanceledError') return;
        // Never the error object in the console: axios keeps the request, token included.
        if (!mountedRef.current) {
          // The section is gone (tab change, panel closed): a refusal such as
          // PENNYLANE_APPENDIX_UNCONFIRMED must still reach the admin.
          toast.error(appendixErrorText(text, errorCodeOf(error), 'addFailed'));
          return;
        }
        setUploadingName(null);
        showFailure(errorCodeOf(error), 'addFailed');
      },
    );
  };

  const takeFiles = (files) => {
    if (isUploading) return;
    const list = Array.from(files ?? []);
    if (list.length === 0) return;
    if (list.length > 1) {
      showFailure(null, 'oneFileOnly');
      return;
    }
    upload(list[0]);
  };

  const onPick = (event) => {
    takeFiles(event.target.files);
    // The same file picked again must fire a change.
    event.target.value = '';
  };
  const onDragOver = (event) => {
    event.preventDefault();
    if (!isUploading) setIsDragging(true);
  };
  const onDragLeave = () => setIsDragging(false);
  const onDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    takeFiles(event.dataTransfer?.files);
  };

  const download = (appendix) => {
    const abortKey = appendixFileAbortKey(appendix.id);
    downloadKeysRef.current.add(abortKey);
    setDownloadingIds((ids) => [...ids, appendix.id]);
    const done = () => {
      downloadKeysRef.current.delete(abortKey);
      if (mountedRef.current)
        setDownloadingIds((ids) => ids.filter((id) => id !== appendix.id));
    };
    ApiDatabase.getInvoiceAppendixFile(
      { number, appendixId: appendix.id },
      (blob) => {
        done();
        downloadFile(blob, appendix.name);
      },
      (error) => {
        done();
        if (!mountedRef.current || error?.name === 'CanceledError') return;
        const code = errorCodeOf(error);
        showFailure(code, 'downloadFailed');
        if (code === 'APPENDIX_NOT_FOUND') reload();
      },
      { abortKey },
    );
  };

  const confirm = (appendix) => {
    setFailure(null);
    setConfirmingIds((ids) => [...ids, appendix.id]);
    const done = () =>
      setConfirmingIds((ids) => ids.filter((id) => id !== appendix.id));
    ApiDatabase.postInvoiceAppendixConfirm(
      { number, appendixId: appendix.id },
      (data) => {
        if (!mountedRef.current) return;
        done();
        if (data?.appendix) replace(data.appendix);
        else reload();
        toast.success(
          text('confirmed', 'Annexe enregistrée comme envoyée dans Pennylane.'),
        );
        onConfirmed?.();
      },
      (error) => {
        if (!mountedRef.current || error?.name === 'CanceledError') return;
        done();
        const code = errorCodeOf(error);
        showFailure(code, 'confirmFailed');
        if (STALE_LIST_CODES.includes(code)) reload();
      },
    );
  };

  const askDelete = (appendix) => {
    setFailure(null);
    setModalData({ number, appendix });
    toggleModal('invoice_appendix_delete');
  };

  const addLabel = text('add', 'Ajouter une annexe');
  let list = null;
  if (items === null && appendices.failed) {
    list = (
      <div
        role="alert"
        className="flex items-center justify-between gap-3 px-4 py-3 text-sm text-gray-700"
      >
        {text('errors.loadFailed', 'Les annexes n’ont pas pu être chargées.')}
        <Button color="SECONDARY" onClick={reload}>
          {text('retry', 'Réessayer')}
        </Button>
      </div>
    );
  } else if (items === null) {
    list = (
      <div role="status" className="px-4 py-3 text-sm text-gray-500">
        {text('loading', 'Chargement des annexes…')}
      </div>
    );
  } else if (items.length === 0) {
    list = (
      <div className="px-4 py-3 text-sm text-gray-500">
        {text('empty', 'Aucune annexe')}
      </div>
    );
  } else {
    list = (
      <ul aria-label={text('title', 'Annexes')}>
        {items.map((appendix) => {
          const status = STATUSES[appendix.status];
          const author = fullName(appendix.addedBy);
          const addedOn = formatParisDay(appendix.addedAt);
          const meta = [
            formatAppendixSize(text, appendix.size),
            author && fillText(text('by', 'par {name}'), { name: author }),
            addedOn &&
              fillText(text('addedOn', 'ajoutée le {date}'), {
                date: addedOn,
              }),
          ].filter(Boolean);
          const isConfirming = confirmingIds.includes(appendix.id);
          return (
            <li
              key={appendix.id}
              aria-label={appendix.name}
              className="flex flex-col gap-2 px-4 py-3 border-b border-gray-100 last:border-b-0"
            >
              <div className="flex items-center gap-3">
                <DocumentIcon wh={20} color="#6B7280" />
                <div className="flex flex-col min-w-0 flex-1">
                  <span
                    className="truncate text-sm text-gray-900"
                    title={appendix.name}
                  >
                    {appendix.name}
                  </span>
                  <span className="text-xs text-gray-500">
                    {meta.join(' · ')}
                  </span>
                </div>
                <button
                  type="button"
                  className={`${cardIconButtonClass} disabled:opacity-40 disabled:cursor-not-allowed`}
                  aria-label={fillText(text('download', 'Télécharger {name}'), {
                    name: appendix.name,
                  })}
                  disabled={downloadingIds.includes(appendix.id)}
                  onClick={() => download(appendix)}
                >
                  <DownloadIcon />
                </button>
                <button
                  type="button"
                  className={cardIconButtonClass}
                  aria-label={fillText(text('delete', 'Supprimer {name}'), {
                    name: appendix.name,
                  })}
                  onClick={() => askDelete(appendix)}
                >
                  <TrashIcon wh={18} color="#374151" />
                </button>
              </div>
              {status && (
                <span
                  className={`self-start rounded-full px-2 py-0.5 text-xs font-medium ${status.className}`}
                >
                  {text(`status.${appendix.status}`, status.fallback)}
                </span>
              )}
              {appendix.status === 'unconfirmed' && (
                <div className="flex flex-col gap-2 rounded-lg bg-yellow-50 px-3 py-2">
                  <p className="text-xs text-yellow-800">
                    {text(
                      'unconfirmedHelp',
                      'Pennylane n’a pas répondu à son envoi : elle y est peut-être. Cherchez-la d’abord dans Pennylane. Si elle y est, confirmez-le ici ; sinon, supprimez-la et ajoutez-la à nouveau.',
                    )}
                  </p>
                  <Button
                    color={isConfirming ? 'DISABLED' : 'SECONDARY'}
                    className="self-start"
                    disabled={isConfirming}
                    onClick={() => confirm(appendix)}
                  >
                    {isConfirming
                      ? text('confirming', 'Confirmation…')
                      : text('confirm', 'Elle est bien dans Pennylane')}
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <PanelCard
      title={text('title', 'Annexes')}
      badge={
        <button
          type="button"
          className={`${cardIconButtonClass} disabled:opacity-40 disabled:cursor-not-allowed`}
          aria-label={addLabel}
          title={addLabel}
          disabled={isUploading}
          onClick={() => inputRef.current?.click()}
        >
          <Plus wh={18} color="#374151" />
        </button>
      }
      footer={
        <div className="flex flex-col gap-2">
          <input
            ref={inputRef}
            type="file"
            accept={APPENDIX_ACCEPT}
            className="hidden"
            data-testid="invoice-appendix-input"
            tabIndex={-1}
            aria-hidden
            disabled={isUploading}
            onChange={onPick}
          />
          <div
            data-testid="invoice-appendix-dropzone"
            aria-disabled={isUploading}
            onDragOver={onDragOver}
            onDragEnter={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className={`flex flex-col items-center gap-1 rounded-lg border border-dashed px-4 py-4 text-center ${
              isDragging ? 'border-yellow-500 bg-yellow-50' : 'border-gray-300'
            } ${isUploading ? 'opacity-60' : ''}`}
          >
            {isUploading ? (
              <span role="status" className="text-sm text-gray-700">
                {fillText(text('uploading', 'Envoi de {name}…'), {
                  name: uploadingName,
                })}
              </span>
            ) : (
              <span className="text-sm text-gray-700">
                {text(
                  'dropHere',
                  'Glissez une annexe ici, ou utilisez le « + »',
                )}
              </span>
            )}
            <span className="text-xs text-gray-500">
              {text('accepted', 'PDF, JPG ou PNG · 10 Mo max')}
            </span>
          </div>
          {failure && (
            <div
              role="alert"
              className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {failure}
            </div>
          )}
        </div>
      }
    >
      {list}
    </PanelCard>
  );
};

export default InvoiceAppendices;
